<template>
    <component
        :is="component"
        :to="to"
        :href="href || resolvedRoute"
        :disabled="disabled"
        :loading="loading"
        class="d-inline-flex align-items-center border-0 m-0 p-0 outline-none transition-color transition-duration-25"
        :class="[
            (to || href) && 'button',
            bold && 'font-weight-semi-bold',
            disabled && `btn-${shape}-disabled`,
            loading && 'cursor--wait',
            shape === 'text' ? 'btn-text' : 'btn-icon',
            themeClasses,
            uppercase && 'text-uppercase',
            css(styles.ringPrimaryFocusVisible),
        ]"
        @click="clickEvent"
    >
        <template v-if="shape === 'icon'">
            <template v-if="!loading">
                <v-icon
                    v-if="icon"
                    :name="icon"
                    :w="iconSize"
                    :flip="flipIcon"
                />

                <slot v-else />
            </template>

            <v-icon
                v-else
                name="loading"
                :w="iconSize"
                spin
                :flip="flipIcon"
            />
        </template>

        <template v-else>
            <slot v-if="!loading" />

            <div v-else>{{ $t("common.loading") }}...</div>
        </template>
    </component>
</template>

<script setup>
import { computed, toRefs } from "vue"
import { useStore } from "vuex"
import { css } from "~/lib/aphrodite"
import { useButton } from "./useButton"

const props = defineProps({
    to: {
        type: [String, Object],
        default: null,
    },
    href: {
        type: String,
        default: "",
    },
    disabled: {
        type: Boolean,
        default: false,
    },
    flipIcon: {
        type: String,
        validator: val => {
            const OK = ["horizontal", "vertical"].includes(val)
            if (!OK) console.trace(`Invalid prop: ${val}`)
            return OK
        },
    },
    icon: {
        type: String,
        default: "",
    },
    loading: {
        type: Boolean,
        default: false,
    },
    tag: {
        type: String,
        default: "button",
        validator: val => ["button", "div", "span"].includes(val),
    },
    bold: {
        type: Boolean,
        default: false,
    },
    iconSize: {
        type: Number,
        default: 24,
    },
    shape: {
        type: String,
        default: "icon",
        validator: val => ["icon", "text"].includes(val),
    },
    theme: {
        type: String,
        default: "primary",
        validator: val => {
            const OK = [
                "primary",
                "success",
                "danger",
                "warning",
                "dark",
                "light",
                "gray-dark",
                "gray-medium",
            ].includes(val)
            if (!OK) console.trace(`Invalid theme ${val}`)
            return OK
        },
    },
    uppercase: {
        type: Boolean,
        default: false,
    },
})

const emits = defineEmits(["click"])

const { to, href, tag, loading } = toRefs(props)

const { component, resolvedRoute, clickEvent } = useButton(
    to,
    href,
    tag,
    loading,
    emits
)
const store = useStore()
const styles = store.getters["theme/styles"]

const themeClasses = computed(() => {
    const { loading, theme } = props
    const linkPrimary = css(styles.linkPrimary)
    const linkPrimaryLoading = css(styles.linkPrimaryLoading)

    switch (theme) {
        case "primary":
            return loading ? linkPrimaryLoading : linkPrimary

        case "success":
        case "danger":
        case "warning":
        case "info":
        case "dark":
        case "light":
        case "gray-dark":
        case "gray-medium":
            return loading
                ? [
                      `text-link-${theme}-loading hover:text-link-${theme}-loading`,
                  ]
                : [
                      `text-${theme} hover:text-link-${theme} active:text-${theme}`,
                  ]

        default:
            throw new Error(`theme ${theme} is undefined ツ`)
    }
})
</script>
